import { useRef } from "react";
import { Link } from "react-router";
import { getFormattedCaaSImageUrl } from "@components/OptimisedImage/helper";
import PreloadLCPImage from "@components/OptimisedImage/PreloadLCPImage";
import { useOnLoadImages } from "@hooks/useOnLoadImages";
import { FeedMedia } from "@pages/Article/utils/types";
import { CaaSImageFilters } from "@typings/OptimisedImage";
import { cn } from "@util/helpers";

type ArticleThumbnailProps = {
  className?: string;
  articleId?: string;
  media: FeedMedia;
  filters?: CaaSImageFilters;
  orientation?: "landscape" | "square" | "original" | "portrait";
  withBlurBackground?: boolean;
  imageClassName?: string;
  articleLink?: string;
  preloadLCPImage?: boolean;
};

type ImageContainerProps = {
  withBlurBackground: boolean;
  imgSrc: string;
  imageClassName?: string;
  media: FeedMedia;
  image: FeedMedia["landscape"];
};

const ImageContainer = ({
  withBlurBackground,
  imgSrc,
  imageClassName,
  media,
  image,
}: ImageContainerProps) => {
  return (
    <>
      {withBlurBackground ? (
        <div
          style={{
            background: `center / cover no-repeat url(${imgSrc})`,
          }}
          className="absolute top-0 z-[-1] h-full w-full overflow-hidden blur-[100px]"
        />
      ) : null}

      <img
        className={cn(
          "relative z-10 h-full w-full object-contain",
          imageClassName
        )}
        src={imgSrc}
        alt={media.caption || ""}
        width={image?.width || 0}
        height={image?.height || 0}
      />
    </>
  );
};

const ArticleThumbnail = ({
  className,
  articleLink,
  media,
  filters,
  orientation = "original",
  withBlurBackground = false,
  imageClassName,
  preloadLCPImage = false,
}: ArticleThumbnailProps): React.ReactElement => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const imagesLoaded = useOnLoadImages(wrapperRef);

  const defaultFilters: CaaSImageFilters = {
    w: 1570,
    h: 1046,
    dpr: 1,
    f: "webp",
  };

  const image = media[orientation] || media["landscape"] || media["original"];

  if (!image || !image.url) {
    return <></>;
  }

  const imgSrc = getFormattedCaaSImageUrl(image.url, filters || defaultFilters);

  return (
    <>
      {preloadLCPImage ? <PreloadLCPImage imageSource={imgSrc} /> : null}

      <div
        className={cn(
          "relative w-full overflow-hidden",
          {
            "article-main-image--loaded": imagesLoaded,
          },
          className
        )}
        ref={wrapperRef}
        data-testid="article-thumbnail-component"
      >
        {articleLink ? (
          <Link to={articleLink} className="block h-full w-full">
            <ImageContainer
              withBlurBackground={withBlurBackground}
              imgSrc={imgSrc}
              imageClassName={imageClassName}
              media={media}
              image={image}
            />
          </Link>
        ) : (
          <ImageContainer
            withBlurBackground={withBlurBackground}
            imgSrc={imgSrc}
            imageClassName={imageClassName}
            media={media}
            image={image}
          />
        )}
      </div>
    </>
  );
};

export default ArticleThumbnail;
